import _ from 'underscore';
import {
  useCallback, useEffect, useMemo, useReducer, useRef,
} from 'react';
import type {
  DataRefId,
  EntityObject,
  OverreactSpec,
  RequestOptions,
} from '../public-types';
import { useEnvironment } from '../environment';
import {
  createDataNode,
  getDataNode,
  getDataRef,
  getRecords,
  getRecordsById,
  updateDataRefWithIds,
} from '../store';
import type { Store } from '../store';
import { useDeepEqualEffect, useDeepMemo } from '../utils/primitive-utilities';
import { getDataFromRecords, getLookupCacheFn } from './helper';
import type { LookupCacheFn } from './helper';
import { getCacheIds } from './lookup-cache';
import { OverreactRequest } from './overreact-request';
import { useComponent } from './use-component';

export interface FetchConfig<TEntity extends EntityObject, TVariables extends object> {
  postponeRead?: boolean;
  lookupCacheByVariables?: LookupCacheFn<TEntity, TVariables>;
}

type VariablesWithOptions = object & { options?: RequestOptions };

export function equalityFn<TValue>(newData: TValue, oldData: TValue) {
  return _.isEqual(newData, oldData);
}

export function useFetch<
  TEntity extends EntityObject,
  TVariables extends VariablesWithOptions,
>(
  dataRefId: DataRefId,
  spec: OverreactSpec<TVariables, null, TEntity | TEntity[], TEntity>,
  variables?: TVariables,
  config?: FetchConfig<TEntity, TVariables>,
): [TEntity | TEntity[] | undefined, unknown] {
  const componentName = useComponent();
  const memoedConfig = useDeepMemo(config);
  const { requestContract, responseContract, environmentLookupFn } = spec;
  const requestRequired = useRef(true);
  const currentData = useRef<TEntity | TEntity[]>();
  const currentError = useRef<unknown>();
  const [, forceRender] = useReducer(value => value + 1, 0);
  const environment = useEnvironment(environmentLookupFn);
  const { postponeRead, lookupCacheByVariables } = memoedConfig || {};
  const fetchPolicy = variables?.options?.fetchPolicy || requestContract.fetchPolicy;
  const lookupFn = useMemo(
    () => getLookupCacheFn(lookupCacheByVariables, spec, fetchPolicy),
    [fetchPolicy, lookupCacheByVariables, spec],
  );

  const dataCallback = useCallback(() => {
    if (!environment) {
      return;
    }
    const records = getRecords(
      environment.store as Store<TEntity>,
      requestContract,
      dataRefId,
    );
    const dataRef = getDataRef(environment.store, requestContract, dataRefId);
    dataRef?.registerComponent({ componentName });
    if (records) {
      const data = getDataFromRecords(records, responseContract);
      if (!equalityFn(data, currentData.current)) {
        currentData.current = data;
        forceRender();
      }
    }
  }, [componentName, dataRefId, environment, requestContract, responseContract]);

  const errorCallback = useCallback(() => {
    if (!environment) {
      return;
    }
    const error = getDataRef(environment.store, requestContract, dataRefId)?.status.error;
    if (!equalityFn(error, currentError.current)) {
      currentError.current = error;
      forceRender();
    }
  }, [dataRefId, environment, requestContract]);

  const dataObserver = useMemo(() => ({
    update: dataCallback,
    onError: errorCallback,
  }), [dataCallback, errorCallback]);

  useEffect(() => {
    if (!environment) {
      return _.noop;
    }
    const schemaNode = requestContract.getSchemaNode();
    const dataNode = getDataNode(schemaNode) || createDataNode(schemaNode, environment.store);
    const dataRef = dataNode.getDataRef(dataRefId);
    dataRef.subscribe(dataObserver);
    return () => dataRef.unsubscribe(dataObserver);
  }, [dataObserver, dataRefId, environment, requestContract]);

  useEffect(() => {
    if (environment && !currentData.current && lookupFn) {
      try {
        const store = environment.store as Store<TEntity>;
        const overreactIds = getCacheIds({
          store,
          requestContract,
          variables: variables || {} as TVariables,
          lookupFn,
        });
        if (!_.isEmpty(overreactIds)) {
          const dataRef = getDataRef(store, requestContract, dataRefId);
          if (dataRef) {
            updateDataRefWithIds(dataRef, overreactIds);
          }
          const records = getRecordsById(store, requestContract, overreactIds);
          currentData.current = getDataFromRecords(records, responseContract);
          requestRequired.current = false;
          forceRender();
        }
      } catch (error) {
        // TODO: log error and send request
      }
    }
  // we only try hit cache once when environment is ready
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment]);

  useDeepEqualEffect(() => {
    if (requestRequired.current && environment && !postponeRead && variables) {
      environment.pushRequest(new OverreactRequest({
        id: dataRefId,
        requestContract,
        spec,
        variables,
        data: null,
        componentName,
      }));
      requestRequired.current = false;
    }
  }, [dataCallback, environment, requestContract, spec, variables]);

  return [currentData.current, currentError.current];
}
